import { useEffect, useRef } from "react";
import { TDDListItem } from "../../data/tdd/tddList";

interface TDDListSidebarProps {
  tddList: TDDListItem[];
  activeTdd: string;
  onSelectTdd: (tddId: string) => void;
}

const statusLabels: Record<string, string> = {
  "spec-ready": "SPEC READY",
  "in-development": "IN DEVELOPMENT",
  "planned": "PLANNED",
  "deployed": "DEPLOYED",
};

export function TDDListSidebar({
  tddList,
  activeTdd,
  onSelectTdd,
}: TDDListSidebarProps) {
  const activeRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Smooth scroll sync for active item
  useEffect(() => {
    if (activeRef.current && containerRef.current) {
      const container = containerRef.current;
      const activeElement = activeRef.current;
      const containerRect = container.getBoundingClientRect();
      const activeRect = activeElement.getBoundingClientRect();
      
      const scrollTop = container.scrollTop;
      const activeTop = activeRect.top - containerRect.top + scrollTop;
      const containerHeight = container.clientHeight;
      const activeHeight = activeRect.height;
      
      // Center the active item in viewport
      const targetScroll = activeTop - (containerHeight / 2) + (activeHeight / 2);
      
      container.scrollTo({
        top: targetScroll,
        behavior: 'smooth',
      });
    }
  }, [activeTdd]);

  return (
    <div 
      ref={containerRef}
      className="w-full lg:sticky lg:top-6 lg:self-start overflow-visible lg:overflow-y-auto max-h-none lg:max-h-[calc(100vh-120px)]"
    >
      <div className="space-y-0.5">
        {tddList.map((tdd) => {
          const isActive = tdd.id === activeTdd;
          return (
            <button
              key={tdd.id}
              ref={isActive ? activeRef : null}
              onClick={() => onSelectTdd(tdd.id)}
              className={`tdd-sidebar-button group relative w-full text-left transition-all duration-[120ms] ease-out ${
                isActive
                  ? "text-white"
                  : "text-white/70 hover:text-white"
              }`}
              style={{
                transitionProperty: "color, transform",
              }}
            >
              {/* Active highlight - left border glow */}
              {isActive && (
                <div
                  className="absolute left-0 top-0 h-full w-[2px] bg-[rgba(0,226,255,0.4)] transition-opacity duration-[120ms] ease-out"
                  style={{
                    boxShadow: "inset 2px 0 0 0 rgba(0, 226, 255, 0.4)",
                  }}
                />
              )}
              
              {/* Hover border glow */}
              <div
                className={`absolute left-0 top-0 h-full w-[2px] bg-[rgba(0,226,255,0.2)] transition-all duration-[120ms] ease-out ${
                  isActive
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0"
                }`}
              />

              <div
                className={`relative px-4 py-2.5 transition-all duration-[120ms] ease-out ${
                  isActive
                    ? "bg-white/8"
                    : "bg-transparent group-hover:bg-white/5"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/50 mb-1">
                      {tdd.id}
                    </p>
                    <h3 className="font-serif text-[15px] font-medium leading-tight">
                      {tdd.title}
                    </h3>
                  </div>
                  <span
                    className={`font-mono text-[9px] uppercase tracking-[0.1em] text-white/45 flex-shrink-0 ${
                      isActive ? "text-white/55" : ""
                    }`}
                  >
                    {statusLabels[tdd.status] || tdd.status}
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

